/**
 * `[<resource>, <scope?>, <id-or-filter?>]`, mirroring REST paths.
 *
 * Centralised here so a mutation's invalidation set is written against the
 * same literal the read used. A key spelled twice is a cache that never
 * invalidates on one of the two spellings.
 */

import type { EngagementSubject } from '@shared/engagement/subject';

export const queryKeys = {
  auth: {
    configuration: () => ['auth', 'configuration'] as const,
    me: () => ['auth', 'me'] as const,
    permissions: (service: string) => ['auth', 'permissions', service] as const,
  },
  content: {
    shelvesHome: () => ['content', 'shelves', 'home'] as const,
    categories: () => ['content', 'categories'] as const,
    tags: () => ['content', 'tags'] as const,
    tagStrip: () => ['content', 'tagStrip'] as const,
    item: (itemId: string) => ['content', 'item', itemId] as const,
    myCollections: () => ['content', 'me', 'collections'] as const,
    collection: (collectionId: string) => ['content', 'collection', collectionId] as const,
    saveTargets: (itemId: string) => ['content', 'saveTargets', itemId] as const,
    profileCollections: (handle: string) => ['content', 'profileCollections', handle] as const,
    /** The editorial listing — ONE unfiltered reverse-chronological stream, so
     *  the key carries no facet to vary on. */
    editorialPosts: () => ['content', 'editorialPosts'] as const,
    /** Keyed on the SLUG, because that is what the route resolves on. */
    editorialPost: (slug: string) => ['content', 'editorialPost', slug] as const,
    /** A credited person's own profile. Keyed on the SLUG for the same reason
     *  the editorial post is: the route resolves on it and the ULID comes back
     *  inside the payload. */
    creditHolder: (slug: string) => ['content', 'creditHolder', slug] as const,
    /** Every title that person is credited on — a cursor-paged listing held
     *  under ONE key, so the accumulated grid survives paging. */
    creditedTitles: (slug: string) => ['content', 'creditedTitles', slug] as const,
  },
  assets: {
    playback: (assetId: string) => ['assets', 'playback', assetId] as const,
    renditions: (assetId: string) => ['assets', 'renditions', assetId] as const,
    /** Batch-resolved artwork for a page of editorial cards, keyed on the ids
     *  the page asked about. */
    editorialArtwork: (postIds: readonly string[]) => ['assets', 'editorialArtwork', postIds] as const,
    /**
     * Every artwork the title detail page draws, resolved in ONE call — the
     * title's own hero, each season's thumbnail, and each episode's still.
     *
     * The key carries all three id sets because the page asks about all three
     * subjects at once: an episode list that grew a row must not read a map
     * cached before that row existed.
     */
    titleArtwork: (contentItemId: string, groupIds: readonly string[], assetIds: readonly string[]) =>
      ['assets', 'titleArtwork', contentItemId, groupIds, assetIds] as const,
  },
  finance: {
    plans: () => ['finance', 'plans'] as const,
    plan: (planId: string) => ['finance', 'plan', planId] as const,
    subscription: () => ['finance', 'me', 'subscription'] as const,
    /**
     * What the caller HOLDS, as distinct from the subscription that produced
     * it. One row under catalogue scope — which is every v1 row — and the only
     * read that carries an entitlement's `source` and `scope`, so it is the
     * one the lists surface's entitlement statement reads. A manual grant has
     * no subscription record at all, and would be invisible on the other key.
     */
    entitlements: () => ['finance', 'me', 'entitlements'] as const,
    /** The checkout panel's failure-reason source — `failure_reason` lives here. */
    payments: () => ['finance', 'me', 'payments'] as const,
    access: (itemId: string) => ['finance', 'access', itemId] as const,
    /**
     * Which checkout step the bound provider requires. A property of the
     * platform rather than of the reader, so it is keyed without a principal
     * and shared across every panel that composes a payment form.
     */
    paymentMethod: () => ['finance', 'paymentMethod'] as const,
  },
  engagement: {
    /**
     * BOTH SUBJECTS, and the kind is part of the key rather than assumed away.
     * A ContentItem id and an editorial post id are ULIDs minted by two
     * different services, so nothing guarantees they cannot collide — and a
     * collision would serve one subject's counters under the other's key.
     */
    counters: (subject: EngagementSubject) => ['engagement', 'counters', subject.kind, subject.id] as const,
    comments: (subject: EngagementSubject) => ['engagement', 'comments', subject.kind, subject.id] as const,
    /**
     * Replies walk their OWN cursor over a disjoint set — a root's replies are
     * not members of the paged root set, so expanding a thread never consumes
     * the root cursor and paging never collapses an expanded thread.
     */
    replies: (commentId: string) => ['engagement', 'replies', commentId] as const,
  },
  notification: {
    list: (filters: Readonly<Record<string, string | number>>) => ['notification', 'list', filters] as const,
    unreadCount: () => ['notification', 'unreadCount'] as const,
    channelPreferences: () => ['notification', 'channelPreferences'] as const,
  },
  support: {
    cases: () => ['support', 'cases'] as const,
    case: (caseId: string) => ['support', 'case', caseId] as const,
    caseTypes: () => ['support', 'caseTypes'] as const,
    caseStates: () => ['support', 'caseStates'] as const,
  },
  analytics: {
    /** Read counts for a page of listing rows, keyed on the ids the page asked
     *  about — a different page is a different question. */
    editorialReadCounts: (postIds: readonly string[]) =>
      ['analytics', 'editorialReadCounts', postIds] as const,
    continueWatching: () => ['analytics', 'me', 'continueWatching'] as const,
    playbackPosition: (assetId: string) => ['analytics', 'playbackPosition', assetId] as const,
    /**
     * The BATCH read behind the season-grouped episode list: one row per asset
     * in one call, so every episode draws its own progress bar and completion
     * marker without one request per row. A Film asks for one id and gets one
     * row — the same shape, not a special case.
     */
    playbackPositions: (itemId: string) => ['analytics', 'playbackPositions', itemId] as const,
    creditRow: (itemId: string) => ['analytics', 'creditRow', itemId] as const,
  },
  search: {
    shelfItems: (source: string, parameter?: string) => ['search', 'shelfItems', source, parameter] as const,
    filters: () => ['search', 'filters'] as const,
    /**
     * One listing, keyed on the ACTIVE facets in a stable order.
     *
     * The cursor is deliberately absent: `useInfiniteQuery` holds every page of
     * one listing under one key, and folding the cursor in would give each page
     * its own entry and lose the accumulated grid on every «اعرض المزيد».
     */
    results: (facets: readonly (readonly [string, string])[]) => ['search', 'results', facets] as const,
  },
} as const;

/**
 * `staleTime` policy (PRD-TDD §10.2). Three keys sit at 0 on purpose: a fresh
 * subscription or a changed entitlement must be reflected immediately, and a
 * stale `access` answer is the one cache miss that renders the wrong player.
 */
export const staleTimes = {
  /** Lists and rails. */
  list: 30_000,
  /** Served vocabularies: auth configuration, case types and states, categories, tags, facets. */
  vocabulary: 5 * 60_000,
  /** `finance.access`, `finance.me.subscription`, and the title detail. */
  live: 0,
} as const;
